import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Eye, EyeOff, AlertCircle, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import { GoogleLogin } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";
import { GLOBAL_IMAGES } from "../../assets/asset";
import useLogin, { finalizeStudentLogin } from "../../features/auth/useLogin";
import { startJourney, logLoginSubmitted } from "../../utils/datadogLogger";
import useResponsiveGoogleWidth from "../../hooks/useResponsiveGoogleWidth";
import {
  extractStudentJwtToken,
  extractStudentUserRecord,
  studentAuthApi,
} from "../../features/auth/authApi";

const GENERIC_GOOGLE_LOGIN_ERROR = "Unable to sign in with Google. Please try again.";

const LoginForm = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const loginMutation = useLogin();
  const [googleError, setGoogleError] = useState("");
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);

  const [formData, setFormData] = useState({ email: "", password: "" });
  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const { containerRef: googleButtonRef, buttonWidth: googleButtonWidth } =
    useResponsiveGoogleWidth();

  // SECURITY: refs to manage focus safely after failures
  const emailRef = useRef(null);
  const passwordRef = useRef(null);

  // SECURITY: local brute-force throttle (client-side)
  const MAX_ATTEMPTS = 5;
  const LOCK_MINUTES = 5;
  const [attempts, setAttempts] = useState(0);
  const [lockUntil, setLockUntil] = useState(null);

  // SECURITY: sanitize helper – trims + removes control characters
  const sanitizeInput = (v) => v.replace(/[^\x20-\x7E]/g, "").trim();

  // SECURITY: stronger email validator (basic regex + length cap)
const validateEmail = (email) => {
    // Check for length limit (RFC standard)
    if (email.length > 254) {
        return false;
    }
    
    // Check for double at symbols
    if (email.includes('@@')) {
        return false;
    }
    
    // Check for double dots
    if (email.includes('..')) {
        return false;
    }
    
    // Regex validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
};


  const validatePassword = (pwd) => pwd.length >= 8 && pwd.length <= 128;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const clean = sanitizeInput(value);
    setFormData((prev) => ({ ...prev, [name]: clean }));
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // SECURITY: double-submit guard + throttle checks
  const newHandleLogin = async () => {
    // Start journey for login
    startJourney('login');

    // Log login submitted
    logLoginSubmitted('email');

    // Prevent accidental double click while pending
    if (loginMutation.isPending) return;

    // Throttle check
    if (lockUntil && Date.now() < lockUntil) {
      const secs = Math.ceil((lockUntil - Date.now()) / 1000);
      setErrors((prev) => ({
        ...prev,
        submit: `Too many failed attempts. Try again in ${secs}s.`,
      }));
      return;
    }

    const newErrors = {};
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!validateEmail(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.password) {
      newErrors.password = "Password is required";
    } else if (!validatePassword(formData.password)) {
      newErrors.password = "Password must be 8–128 characters";
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      // SECURITY: focus the first invalid field
      if (newErrors.email && emailRef.current) emailRef.current.focus();
      else if (newErrors.password && passwordRef.current)
        passwordRef.current.focus();
      return;
    }

    // SECURITY: final sanitize before submission (defense in depth)
    const payload = {
      email: sanitizeInput(formData.email),
      password: sanitizeInput(formData.password),
    };

    // Note: not removing your code; just passing callbacks to track success/failure.
    loginMutation.mutate(payload, {
      onSuccess: () => {
        // SECURITY: reset lock and attempts on success
        setAttempts(0);
        setLockUntil(null);
      },
      onError: () => {
        // SECURITY: increment attempts and optionally lock
        const next = attempts + 1;
        setAttempts(next);
        if (next >= MAX_ATTEMPTS) {
          setLockUntil(Date.now() + LOCK_MINUTES * 60 * 1000);
        }
        // SECURITY: clear password on error to avoid lingering sensitive data
        setFormData((prev) => ({ ...prev, password: "" }));
        setShowPassword(false);
        if (passwordRef.current) passwordRef.current.focus();
      },
    });
  };

  const handleGoogleLoginSuccess = async (credentialResponse) => {
    setGoogleError("");
    setIsGoogleLoading(true);

    try {
      const idToken = credentialResponse.credential;
      if (!idToken) {
        setGoogleError(GENERIC_GOOGLE_LOGIN_ERROR);
        setIsGoogleLoading(false);
        return;
      }

      let email = "";
      try {
        const decoded = jwtDecode(idToken);
        email = decoded.email || "";
      } catch (_e) {
        email = "";
      }

      const response = await studentAuthApi.googleLogin({
        id_token: idToken,
        email,
      });

      const profileData = extractStudentUserRecord(response, {
        jwtoken: extractStudentJwtToken(response),
      });

      if (!profileData.jwtoken) {
        setGoogleError(GENERIC_GOOGLE_LOGIN_ERROR);
        setIsGoogleLoading(false);
        return;
      }

      finalizeStudentLogin({ dispatch, navigate, profileData });
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message || GENERIC_GOOGLE_LOGIN_ERROR;
      setGoogleError(errorMessage);
      toast.error(errorMessage, {
        ariaLabel: "Google Login Error",
        autoClose: 2500,
        theme: "colored",
        hideProgressBar: true,
        className: "font-mali",
      });
    } finally {
      setIsGoogleLoading(false);
    }
  };

  const handleGoogleLoginError = () => {
    setGoogleError(GENERIC_GOOGLE_LOGIN_ERROR);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    newHandleLogin();
  };

  const handleForgotPassword = () => {
    navigate("/forgot-password");
  };

  // SECURITY: watch mutation error state (in case hook uses global handlers)
  useEffect(() => {
    if (loginMutation.isError) {
      // keep error message generic; server should not leak detail
      setErrors((prev) => ({
        ...prev,
        submit: "Login failed. Please check your credentials and try again.",
      }));
    }
  }, [loginMutation.isError]);

  const buttonClass =
    "flex items-center justify-center rounded-lg py-2 transition duration-300";

  // SECURITY: compute remaining lock time for button text/aria
  const isLocked = lockUntil && Date.now() < lockUntil;
  const remainingSeconds = isLocked
    ? Math.ceil((lockUntil - Date.now()) / 1000)
    : 0;

  return (
    <div
      className="login-page min-h-screen flex items-center justify-center bg-blue-50 font-mali"
      style={{
        backgroundImage: `url(${GLOBAL_IMAGES.onboarding_bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
      role="main"
      aria-labelledby="login-title"
    >
      <div
        className="login-page-back absolute top-5 left-5 cursor-pointer"
        onClick={() => navigate(-1)}
        role="button"
        aria-label="Go back to the previous page"
      >
        <img
          src={GLOBAL_IMAGES.LoginBackButton}
          alt="Back"
          className="login-page-back-img w-10 h-10"
        />
      </div>
      <div
        className="login-page-card bg-white bg-opacity-60 p-12 rounded-3xl shadow-lg w-full max-w-xl px-4"
        role="region"
        aria-labelledby="login-title"
        aria-describedby="login-description"
      >
        <h2
          id="login-title"
          className="login-page-title text-3xl font-bold text-center text-gray-800 mb-6"
        >
          Log in
        </h2>
        <p id="login-description" className="login-page-description text-center text-gray-600 mb-8">
          Login to save your kids' progress as they work through courses on the
          <span className="font-bold text-indigo-600"> MATHDINGO</span>{" "}
          application
        </p>
        <form className="login-page-form flex flex-col md:space-y-4 w-full max-w-[400px] mx-auto" onSubmit={handleSubmit}>
          {/* <div className="login-page-field mb-4">
            <label
              className="login-page-label block text-gray-700 font-bold mb-2"
              htmlFor="email"
            >
              Email
            </label>
            <input
              type="email"
              id="email"
              name="email"
              ref={emailRef} // SECURITY: focus management
              value={formData.email}
              onChange={handleInputChange}
              className="login-page-input w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter Email Address"
              // SECURITY: safer input hints
              autoComplete="email"
              inputMode="email"
              spellCheck={false}
              autoCapitalize="none"
              autoCorrect="off"
              maxLength={254} // SECURITY: hard cap typical email length
            />
            {errors.email && (
              <div className="login-page-error flex items-center gap-2 mt-2 text-red-700 text-sm">
                <AlertCircle size={16} />
                <span>{errors.email}</span>
              </div>
            )}
          </div>

          <div className="login-page-field mb-4">
            <label
              className="login-page-label block text-gray-700 font-bold mb-2"
              htmlFor="password"
            >
              Password
            </label>
            <div className="login-page-password-wrap relative">
              <input
                type={showPassword ? "text" : "password"}
                id="password"
                name="password"
                ref={passwordRef} // SECURITY: focus management
                value={formData.password}
                onChange={handleInputChange}
                className="login-page-input w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter Password"
                // SECURITY: safer input hints
                autoComplete="current-password"
                spellCheck={false}
                autoCapitalize="none"
                autoCorrect="off"
                maxLength={128} // SECURITY: cap password length to prevent abuse/DoS on server
                enterKeyHint="done"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="login-page-password-toggle absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            {errors.password && (
              <div className="login-page-error flex items-center gap-2 mt-2 text-red-700 text-sm">
                <AlertCircle size={16} />
                <span>{errors.password}</span>
              </div>
            )}
          </div>

          <div className="login-page-forgot text-right mb-4">
            <button
              onClick={handleForgotPassword}
              className="login-page-forgot-button text-blue-800 text-sm font-semibold hover:underline"
              aria-label="Reset your password"
              type="button" // SECURITY: ensure this is not treated as submit by browsers
            >
              Forgot password?
            </button>
          </div>

          {errors.submit && (
            <div className="login-page-submit-error flex items-center gap-2 mb-2 px-4 py-2 bg-red-50 border border-red-300 rounded-lg text-red-700 text-sm">
              <AlertCircle size={16} />
              <span>{errors.submit}</span>
            </div>
          )}

          <button
            disabled={loginMutation.isPending || isLocked}
            className={`login-page-submit ${buttonClass} bg-blue-800 text-white hover:bg-blue-900 w-full ${
              isLocked ? "opacity-60 cursor-not-allowed" : ""
            }`}
            aria-label={
              isLocked
                ? `Locked. Try again in ${remainingSeconds}s.`
                : "Login to your account"
            }
            type="submit"
          >
            {loginMutation.isPending ? (
              <Loader2 className="animate-spin mr-2" size={20} />
            ) : null}
            {isLocked ? `Locked (${remainingSeconds}s)` : "Login"}
          </button> */}

          <button
            type="button"
            onClick={() => navigate("/login-otp")}
            className="flex items-center justify-center rounded-lg py-2 transition duration-300 bg-white text-blue-800 border border-blue-300 hover:bg-blue-50 w-full"
          >
            Continue with OTP
          </button>

          {/* Google Sign In divider and button */}
          <div className="login-page-or-divider flex items-center gap-3 my-3">
            <div className="flex-1 border-t border-gray-400"></div>
            <span className="text-gray-500 text-sm font-semibold">or continue with</span>
            <div className="flex-1 border-t border-gray-400"></div>
          </div>

          <div className="login-page-google auth-google-shell flex flex-col items-center">
            {isGoogleLoading ? (
              <button
                disabled
                type="button"
                className="flex items-center justify-center rounded-lg py-2 bg-white text-gray-700 border border-gray-300 w-full opacity-60 cursor-not-allowed transition duration-300"
              >
                <Loader2 className="animate-spin mr-2" size={20} />
                Signing in...
              </button>
            ) : (
              <div
                ref={googleButtonRef}
                className="auth-google-button-wrap flex min-h-[44px] w-full justify-center"
              >
                <GoogleLogin
                  onSuccess={handleGoogleLoginSuccess}
                  onError={handleGoogleLoginError}
                  text="signin_with"
                  shape="rectangular"
                  size="large"
                  width={googleButtonWidth}
                  logo_alignment="left"
                />
              </div>
            )}
          </div>

          {googleError && (
            <div className="login-page-google-error flex items-center gap-2 mb-2 px-4 py-2 bg-red-50 border border-red-300 rounded-lg text-red-700 text-sm">
              <AlertCircle size={16} />
              <span>{googleError}</span>
            </div>
          )}

          <div className="login-page-signup text-center text-black mt-2">
            <button
              type="button"
              onClick={() => navigate("/createaccount")}
              className="login-page-signup-button text-blue-800 font-semibold hover:underline"
              aria-label="Create a new account"
            >
              Signup
            </button>
            <p className="login-page-new-user text-black md:font-bold mt-2">New user?</p>
          </div>

          <div className="login-page-links flex justify-between mt-4">
            <a
              href="https://mathdingo.com/terms_of_use.html"
              target="_blank"
              rel="noreferrer"
              className="login-page-link text-black font-bold text-sm underline"
              style={{ flex: 1, textAlign: "center" }}
              aria-label="Read subscription use"
            >
              Read Subscription Use
            </a>
            <Link
              to={"/contact"}
              state={{ public: true }}
              className="login-page-link text-black font-bold text-sm underline"
              style={{ flex: 1, textAlign: "center" }}
              aria-label="Contact us for support"
            >
              Contact Us
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default LoginForm;
